import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, RadioTowerIcon, BarChart3Icon, RadioIcon, CpuIcon, ActivityIcon, MenuIcon, Share2Icon, BellIcon, ClockIcon, ZapIcon, LogOutIcon, UserIcon, Loader2Icon } from 'lucide-react';
import { GlobalFilters } from './GlobalFilters';
import { GlobalLoadingBar } from './GlobalLoadingBar';
import { useAuth } from '../contexts/AuthContext';
const navItems = [{
  path: '/',
  label: 'Overview',
  icon: LayoutDashboardIcon
}, {
  path: '/sites',
  label: 'Site Management',
  icon: RadioTowerIcon
}, {
  path: '/linkroute',
  label: 'Link Route',
  icon: Share2Icon
}, {
  path: '/capacity',
  label: 'Capacity Analysis',
  icon: BarChart3Icon
}, {
  path: '/frequency',
  label: 'Frequency Analysis',
  icon: RadioIcon
}, {
  path: '/equipment',
  label: 'Equipment Analysis',
  icon: CpuIcon
}, {
  path: '/status',
  label: 'Status Monitoring',
  icon: ActivityIcon
}];
// Format uptime from a fixed start time
function formatUptime(startMs: number, nowMs: number) {
  const diff = Math.floor((nowMs - startMs) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor(diff % 3600 / 60);
  const s = diff % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
export function Layout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [sessionStart] = useState(() => Date.now());
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const navigate = useNavigate();
  const {
    user,
    signOut
  } = useAuth();
  const userEmail = user?.email ?? '';
  const userInitial = userEmail ? userEmail[0].toUpperCase() : 'U';
  const handleSignOut = async () => {
    setSigningOut(true);
    setUserMenuOpen(false);
    try {
      await signOut();
      navigate('/login', {
        replace: true
      });
    } finally {
      setSigningOut(false);
    }
  };
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const currentNav = navItems.find(item => item.path === location.pathname) || navItems[0];
  const timeStr = now.toLocaleTimeString('en-GB', {
    hour12: false
  });
  const dateStr = now.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short'
  });
  return <div className="flex h-screen overflow-hidden relative" data-id="element-137" data-mp="1qli4cx3hvi72">
      {/* Mobile overlay */}
      <AnimatePresence data-id="element-138" data-mp="1lrdvg8ao503c">
        {sidebarOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-139" data-mp="y2ku1wba201c" />}
      </AnimatePresence>

      {/* SIDEBAR */}
      <aside className={`
          fixed md:relative z-30
          w-64 h-full flex flex-col
          bg-gradient-to-b from-[#0a1428]/95 via-[#071226]/95 to-[#050b1c]/95
          backdrop-blur-2xl
          border-r border-cyan-500/10
          shadow-[8px_0_40px_rgba(0,0,0,0.5)]
          transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `} data-id="element-140" data-mp="2utys9ee7cpqj">
        {/* Brand header */}
        <div className="h-16 flex items-center px-5 border-b border-cyan-500/10 scanline relative" data-id="element-141" data-mp="3iirnt9qwwvkm">
          <div className="flex items-center gap-3" data-id="element-142" data-mp="23213k73axukh">
            <div className="relative" data-id="element-143" data-mp="230g6a9plrxix">
              <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-glow-strong flex items-center justify-center" data-id="element-144" data-mp="273mma92ip32l">
                <img src="https://fzkptteqaooavawzwwlu.supabase.co/storage/v1/object/public/Asset%20Raptor-tif/raptor-tif-web-sidebar-tile.jpg" alt="RAPTOR-TIF" className="w-full h-full object-cover" data-id="element-145" data-mp="2nb950lz3fhrn" />
              </div>
              <div className="absolute -inset-1 rounded-xl bg-cyan-400/20 blur-md -z-10 animate-breathe" data-id="element-146" data-mp="28md2t5jpwyyy" />
            </div>
            <div className="flex flex-col leading-tight min-w-0" data-id="element-147" data-mp="368k7sgjxotbu">
              <span className="font-bold text-base text-white tracking-[0.16em]" data-id="element-148" data-mp="31as3so60lgra">
                RAPTOR
                <span className="text-cyan-400" data-id="element-149" data-mp="2wawb3l2rpbr1">-TIF</span>
              </span>
              <span className="text-[9px] text-slate-500 leading-snug" data-id="element-150" data-mp="2s9u9ixldonlw">
                Radio Planning Tools, Re‑engineering &amp; Inventory
              </span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto py-5 px-3" data-id="element-151" data-mp="1gufdpx4wo1sd">
          <div className="px-3 mb-3" data-id="element-152" data-mp="1gx1k3tg28b90">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.15em]" data-id="element-153" data-mp="3sitmw71sy7tc">
              Monitoring
            </span>
          </div>
          <div className="space-y-1" data-id="element-154" data-mp="bvhfx5tg7px">
            {navItems.map(item => <NavLink key={item.path} to={item.path} onClick={() => setSidebarOpen(false)} className={({
            isActive
          }) => `
                  relative group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                  transition-all duration-300
                  ${isActive ? 'bg-gradient-to-r from-cyan-500/15 via-cyan-500/8 to-transparent text-cyan-300 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.18)]' : 'text-slate-400 hover:bg-slate-800/40 hover:text-slate-100 hover:translate-x-1'}
                `} data-id="element-155" data-mp="5bzz3qign8hq">
                {({
              isActive
            }) => <>
                    {isActive && <motion.span layoutId="nav-indicator" className="nav-indicator" transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30
              }} data-id="element-156" data-mp="2uwcrj7cmq4a8" />}
                    <item.icon size={17} strokeWidth={isActive ? 2.5 : 2} className={`transition-all ${isActive ? 'text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]' : 'group-hover:scale-110'}`} data-id="element-157" data-mp="60c1n8thos8d" />
                    <span className="flex-1" data-id="element-158" data-mp="tpwi9l267deh">{item.label}</span>
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)] animate-pulse" data-id="element-159" data-mp="pqfaum36o66o" />}
                  </>}
              </NavLink>)}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-cyan-500/10 space-y-3" data-id="element-160" data-mp="1ibb01gs8dd1n">
          {/* Account + sign out */}
          <div className="px-2 pt-1" data-id="element-161" data-mp="1nbgsv0a8l5lb">
            <div className="flex items-center gap-2.5 mb-2.5" data-id="element-162" data-mp="1nafasbusziu3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-600/30 border border-cyan-400/40 flex items-center justify-center text-[11px] font-bold text-cyan-200 flex-shrink-0" data-id="element-163" data-mp="18eg2p17p1ioy">
                {userInitial}
              </div>
              <div className="min-w-0 flex-1" data-id="element-164" data-mp="zji4pfpovfqr">
                <p className="text-[11px] text-slate-300 font-medium truncate" data-id="element-165" data-mp="14d4khgr5fzqs">
                  {userEmail || 'Unknown user'}
                </p>
                <p className="text-[9px] text-emerald-400/80 font-mono uppercase tracking-wider" data-id="element-166" data-mp="plc4r3ng0joj">
                  Authenticated
                </p>
              </div>
            </div>

            <button onClick={handleSignOut} disabled={signingOut} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 bg-slate-800/60 border border-slate-700/70 hover:bg-red-500/15 hover:text-red-300 hover:border-red-400/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-167" data-mp="goldd4ju5t0a">
              {signingOut ? <>
                  <Loader2Icon size={13} className="animate-spin" data-id="element-168" data-mp="m5an8a1r42ie" />
                  <span data-id="element-169" data-mp="i4gyxfkecloz">Signing out…</span>
                </> : <>
                  <LogOutIcon size={13} data-id="element-170" data-mp="deabhj1mf78b" />
                  <span data-id="element-171" data-mp="dd8qomusz6de">Sign out</span>
                </>}
            </button>
          </div>

          <div className="pt-2 border-t border-slate-800/60" data-id="element-172" data-mp="8ffhrm247y9f">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5" data-id="element-173" data-mp="3gm51vunkbab">
              <ZapIcon size={12} className="text-cyan-400" data-id="element-174" data-mp="3ulvzbzpojbpq" />
              <span className="font-mono" data-id="element-175" data-mp="3ulck4qrez1fe">Uptime</span>
              <span className="ml-auto text-cyan-300 font-mono" data-id="element-176" data-mp="3qkagzyudm585">
                {formatUptime(sessionStart, now.getTime())}
              </span>
            </div>
            <div className="text-[10px] text-slate-600 text-center font-mono tracking-wider" data-id="element-177" data-mp="264ukm4fdac31">
              © 2026 · RAPTOR-TIF
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col overflow-hidden relative" data-id="element-178" data-mp="2k298v5tvwdtf">
        <GlobalLoadingBar data-id="element-179" data-mp="2k1ho6q1ts0hx" />

        {/* HEADER */}
        <header className="
          relative h-14 md:h-16
          flex items-center justify-between
          px-4 md:px-8
          border-b border-cyan-500/10
          bg-gradient-to-r from-[#050b1c]/80 via-[#071226]/80 to-[#050b1c]/80
          backdrop-blur-xl
          scanline
          z-10
        " data-id="element-180" data-mp="1mfabesvdb1uh">
          <div className="flex items-center gap-4 min-w-0" data-id="element-181" data-mp="214ztq0vldmhz">
            <button className="md:hidden text-slate-300 hover:text-cyan-400 transition-colors" onClick={() => setSidebarOpen(true)} data-id="element-182" data-mp="13osx3tba88sn">
              <MenuIcon size={22} data-id="element-183" data-mp="itnn1c8t35ou" />
            </button>

            <div className="flex items-baseline gap-3 min-w-0" data-id="element-184" data-mp="it435pzli786">
              <h1 className="text-sm md:text-base font-bold text-white tracking-[0.14em] truncate" data-id="element-185" data-mp="nrxjrra6ieck">
                RAPTOR<span className="text-cyan-400" data-id="element-186" data-mp="srjtvrupneh5">-TIF</span>
              </h1>
              <span className="hidden md:inline text-slate-600" data-id="element-187" data-mp="xqmqcnwmkcdi">/</span>
              <span className="hidden md:inline text-sm text-cyan-300/90 font-medium truncate" data-id="element-188" data-mp="xotrhus96wg2">
                {currentNav.label}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-4" data-id="element-189" data-mp="52iibvqkzn7n">
            {/* Live clock */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/80" data-id="element-190" data-mp="9zr0vxfn5t9f">
              <ClockIcon size={13} className="text-cyan-400" data-id="element-191" data-mp="3di298o3s4tjj" />
              <div className="flex flex-col leading-tight" data-id="element-192" data-mp="3dhj11z3xljip">
                <span className="text-xs font-mono text-white font-semibold tracking-wider" data-id="element-193" data-mp="3igltmk2q5x9s">
                  {timeStr}
                </span>
                <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider" data-id="element-194" data-mp="1zl7y01071isk">
                  {dateStr}
                </span>
              </div>
            </div>

            {/* System status */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-id="element-195" data-mp="1qn5r54ts3vxd">
              <span className="pulse-dot bg-emerald-400 text-emerald-400" data-id="element-196" data-mp="1gmwslkcbdyet" />
              <span className="text-xs text-emerald-300 font-semibold hidden md:inline tracking-wide" data-id="element-197" data-mp="1gozrktvg31vo">
                ONLINE
              </span>
            </div>

            {/* Notifications */}
            <button className="relative p-2 rounded-lg hover:bg-slate-800/60 text-slate-400 hover:text-cyan-300 transition-colors" data-id="element-198" data-mp="17sgxhebh97xi">
              <BellIcon size={17} data-id="element-199" data-mp="xx6zr2s43mpj" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.8)]" data-id="element-200" data-mp="xw5lyqjpom44" />
            </button>

            {/* User menu */}
            <div className="relative" data-id="element-201" data-mp="j96x93c8o5t3">
              <button onClick={() => setUserMenuOpen(v => !v)} aria-haspopup="menu" aria-expanded={userMenuOpen} className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-600/30 border border-cyan-400/40 flex items-center justify-center text-xs font-bold text-cyan-200 shadow-glow hover:border-cyan-300/70 transition-colors" data-id="element-202" data-mp="jcb5qcwqlnpk">
                {userInitial}
              </button>

              <AnimatePresence data-id="element-203" data-mp="16yre9gou0szq">
                {userMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} data-id="element-204" data-mp="t7mkuwc24wxu" />
                    <motion.div role="menu" initial={{
                  opacity: 0,
                  y: -6,
                  scale: 0.97
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -6,
                  scale: 0.97
                }} transition={{
                  duration: 0.16
                }} className="absolute right-0 mt-2 w-60 glass-panel rounded-xl p-1.5 z-50" data-id="element-205" data-mp="1kz27urhj0lol">
                      <div className="px-3 py-2.5 border-b border-slate-800/70 mb-1" data-id="element-206" data-mp="3k5ppbnpl06ny">
                        <div className="flex items-center gap-2 text-slate-400 mb-1" data-id="element-207" data-mp="3f8h4ffd1toj0">
                          <UserIcon size={12} data-id="element-208" data-mp="2rgsa5tgo2pzf" />
                          <span className="text-[10px] uppercase tracking-[0.12em] font-semibold" data-id="element-209" data-mp="2rjo83hsilenv">
                            Signed in as
                          </span>
                        </div>
                        <p className="text-xs text-cyan-200 font-mono truncate" data-id="element-210" data-mp="31deqq61ymuvn">
                          {userEmail || 'unknown user'}
                        </p>
                      </div>

                      <button onClick={handleSignOut} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-slate-300 hover:bg-red-500/15 hover:text-red-300 transition-colors" data-id="element-211" data-mp="grmxclbb4cq0">
                        <LogOutIcon size={14} data-id="element-212" data-mp="2me2taedl3ozb" />
                        <span data-id="element-213" data-mp="2qewgt6n471ro">Sign out</span>
                      </button>
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto" data-id="element-214" data-mp="30d2etd5svnr9">
          <div className="p-4 md:p-8 max-w-[1600px] mx-auto space-y-6" data-id="element-215" data-mp="35c5boabmmno5">
            <GlobalFilters data-id="element-216" data-mp="35ac4mtx3q1fm" />
            <AnimatePresence mode="wait" data-id="element-217" data-mp="3a2vhtprvmujw">
              <motion.div key={location.pathname} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -5
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-218" data-mp="3a3x1gimykpd8">
                <Outlet data-id="element-219" data-mp="3t6p75bthd08f" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}